import emailValidator from '../../../tools/emailValidator';

/**
 * List of required field names for the Email Us form.
 * @type {string[]}
 */
export const REQUIRED_EMAIL_US_FIELDS = [
  'contactReason',
  'firstName',
  'lastName',
  'email',
  'orderNum',
  'comment',
];

/**
 * Map of fields to their prevalidation functions.
 * @type {Map<string, { prevalidation: function(string): boolean }>}
 */
export const PREVALIDATION_EMAIL_US_FIELDS = new Map([
  ['email', { prevalidation: emailValidator }],
]);

/**
 * Returns an array of invalid field names based on required fields and prevalidation.
 * A field is considered invalid if:
 *   - It is required and missing from the fields object, or its value is empty/falsy.
 *   - It is present and has a prevalidation function, but fails that prevalidation.
 *
 * @param {Object} fields - The form fields as key-value pairs.
 * @param {string[]} [requiredFields=REQUIRED_EMAIL_US_FIELDS] - List of required field names.
 * @param {Map<string, { prevalidation: function(string): boolean }>} [prevalidationMap=PREVALIDATION_EMAIL_US_FIELDS] - Map of fields to prevalidation functions.
 * @returns {string[]} Array of invalid field names. Includes both missing and invalid fields.
 */
export function getInvalidFields(fields, requiredFields = REQUIRED_EMAIL_US_FIELDS, prevalidationMap = PREVALIDATION_EMAIL_US_FIELDS) {
  const invalidSet = new Set();

  // Check for missing or empty required fields
  requiredFields.forEach((key) => {
    if (!fields[key]) {
      invalidSet.add(key);
    }
  });

  // Check prevalidation for present fields
  Object.entries(fields).forEach(([key, value]) => {
    if (
      prevalidationMap.has(key)
      && !prevalidationMap.get(key).prevalidation(value)
    ) {
      invalidSet.add(key);
    }
  });

  return Array.from(invalidSet);
}

/**
 * Converts a form element into an object of key-value pairs.
 * @param {HTMLFormElement} form - The form element.
 * @returns {Object} The form data as key-value pairs.
 */
export function getFormData(form) {
  return Object.fromEntries([...new FormData(form).entries()]);
}

/**
 * Validates the form and returns an array of invalid field names.
 * @param {Object} formRef - A React ref object pointing to the form element.
 * @param {string[]} [requiredFields=REQUIRED_EMAIL_US_FIELDS] - List of required field names.
 * @param {Map<string, { prevalidation: function(string): boolean }>} [prevalidationMap=PREVALIDATION_EMAIL_US_FIELDS] - Map of fields to prevalidation functions.
 * @returns {string[]} Array of invalid field names.
 */
export function validate(formRef, requiredFields = REQUIRED_EMAIL_US_FIELDS, prevalidationMap = PREVALIDATION_EMAIL_US_FIELDS) {
  const fields = getFormData(formRef.current);
  return getInvalidFields(fields, requiredFields, prevalidationMap);
}
